// src/components/navigation/NavigationBar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Badge, Box, Tooltip, useTheme } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MetricCopyright from '../common/MetricCopyright';

/**
 * NavigationBar component provides main navigation between different sections
 */
const NavigationBar = ({
  currentView = 'concierge',
  onViewChange,
  activeStaffingCount = 0,
  isAdmin = false,
}) => {
  const theme = useTheme();

  const handleViewChange = (view) => {
    if (onViewChange) onViewChange(view);
  };

  const btnStyle = (view, color) => {
    const active = currentView === view;
    return {
      color: active ? color : 'text.secondary',
      bgcolor: active ? `${color}18` : 'transparent',
      border: `1px solid ${active ? `${color}60` : 'rgba(255,255,255,0.12)'}`,
      borderRadius: 1.5,
      px: 2,
      py: 0.75,
      fontSize: '0.8125rem',
      fontWeight: active ? 600 : 400,
      textTransform: 'none',
      letterSpacing: 0,
      transition: 'all 0.15s ease',
      '&:hover': {
        bgcolor: `${color}22`,
        border: `1px solid ${color}80`,
        color,
      },
    };
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        variant="outlined"
        onClick={() => handleViewChange('concierge')}
        sx={btnStyle('concierge', theme.palette.primary.main)}
      >
        Concierge
      </Button>

      <Button
        variant="outlined"
        onClick={() => handleViewChange('staffing')}
        sx={btnStyle('staffing', theme.palette.secondary.main)}
      >
        <Badge
          badgeContent={activeStaffingCount}
          color="secondary"
          invisible={activeStaffingCount === 0}
        >
          Staffing
        </Badge>
      </Button>

      <Tooltip title="Capacity Sold Over Time© (CSOT©)" placement="bottom">
        <Button
          variant="outlined"
          onClick={() => handleViewChange('capacity')}
          sx={btnStyle('capacity', '#FFCF44')}
        >
          <MetricCopyright name="CSOT" />
        </Button>
      </Tooltip>

      {isAdmin && (
        <Button
          variant="outlined"
          onClick={() => handleViewChange('admin')}
          startIcon={<AdminPanelSettingsIcon sx={{ fontSize: '1rem' }} />}
          sx={btnStyle('admin', '#90CAF9')}
        >
          Admin
        </Button>
      )}
    </Box>
  );
};

NavigationBar.propTypes = {
  currentView: PropTypes.oneOf(['concierge', 'staffing', 'capacity', 'admin']),
  onViewChange: PropTypes.func,
  activeStaffingCount: PropTypes.number,
  isAdmin: PropTypes.bool,
};

export default NavigationBar;
